import Footer from '@/components/Footer/footer';
import Constants from '@/Constants';
import { LoginData } from '@/models/LoginData';
import { Profile, profileType } from '@/models/Profile';
import { SamlData } from '@/models/Salm';
import { clearAuth } from '@/services/IdentityService';
import { ApplicationState } from '@/store';
import { authFailureClear, authRequest, forgotPasswordFailureClear, forgotPasswordRequest } from '@/store/identity/action';
import { putPersonRequest } from '@/store/person/action';
import { samlRequest } from '@/store/saml/action';
import React, { ChangeEvent, MouseEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Dispatch } from 'redux';
import * as Yup from 'yup';
import './style.scss';

const schema = Yup.object().shape({
    email: Yup.string()
        .email()
        .required(),
});

const Login: React.FC = () => {
    const [pageReady, setPageReady] = useState<boolean>(false);
    const [byPassAuth, setByPassAuth] = useState<boolean>(false);
    const [logo, setLogo] = useState<string>();

    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [isEmailValid, setIsEmailValid] = useState(false);
    const [activeForm, setActiveForm] = useState('login');
    const [visibilityPasswordType, setVisibilityPasswordType] = useState('password');
    const [visibilityPasswordIcon, setVisibilityPasswordIcon] = useState(Constants.icons.VISIBILITY_OFF);
    const {
        loading,
        hasError,
        error,
        forgotPasswordMode,
        data: { user, person },
    } = useSelector((state: ApplicationState) => state.identity);
    const { data: strings } = useSelector((state: ApplicationState) => state.locale);
    const { organization, application, dynamicAttributes, saml } = useSelector((state: ApplicationState) => ({
        organization: state.appOrganization.data.organization,
        application: state.appOrganization.data.application,
        dynamicAttributes: state.appOrganization.data.dynamicAttributes,
        saml: state.saml,
    }));
    const dispatch: Dispatch = useDispatch();
    const history = useHistory();
    const [selectedProfile, setSelectedProfile] = useState<profileType>('');

    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const hasSupportAccess = params.has('supportAccess');
    const hasSaml = dynamicAttributes && dynamicAttributes.saml;
    const canUseSaml = hasSaml && !hasSupportAccess;

    const handleUsernameChange = (event: ChangeEvent<HTMLInputElement>): void => {
        setUsername(event.target.value);
    };

    const handlePasswordChange = (event: ChangeEvent<HTMLInputElement>): void => {
        setPassword(event.target.value);
    };

    const handleSubmit = (event: MouseEvent): void => {
        event.preventDefault();
        const loginData: LoginData = {
            orgId: organization.id,
            applicationId: Constants.APP_ID,
            username,
            password,
        };
        dispatch(authRequest(loginData));
    };

    const handleForgotPassword = (event: MouseEvent): void => {
        event.preventDefault();
        setActiveForm('forgotPassword');
    };

    const handleSubmitForgotPassword = (event: MouseEvent): void => {
        event.preventDefault();
        const loginData: LoginData = {
            applicationId: Constants.APP_ID,
            username,
        } as LoginData;

        dispatch(forgotPasswordRequest(loginData));
    };

    const handleCancelForgotPassword = (event: MouseEvent): void => {
        event.preventDefault();
        dispatch(forgotPasswordFailureClear());
        setActiveForm('login');
    };

    const handleVisibilityChange = (event: MouseEvent): void => {
        event.preventDefault();
        const icons: string[] = [Constants.icons.VISIBILITY_OFF, Constants.icons.VISIBILITY];
        const types: string[] = ['password', 'text'];

        setVisibilityPasswordType(types[+(visibilityPasswordType === types[0])]);
        setVisibilityPasswordIcon(icons[+(visibilityPasswordIcon === icons[0])]);
    };

    const handleConfirmProfileSelect = (event: MouseEvent): void => {
        event.preventDefault();
        person.dynamicAttributes = person.dynamicAttributes ? { ...person.dynamicAttributes, selectedProfile } : { selectedProfile };
        dispatch(putPersonRequest({ person, history, willClose: false }));
    };

    const handleCancelProfileSelect = (event: MouseEvent): void => {
        event.preventDefault();
        setActiveForm('login');
    };

    useEffect(() => {
        if (organization && organization.orgKey && application && application.key) {
            setLogo(`${Constants.ORG_LOGO.replace('%s', organization.orgKey).replace('%s', application.key.toLowerCase())}?force=${Date.now()}`);
        }
    }, [organization, application]);

    useEffect(() => {
        const authQS = params.get('auth');
        setByPassAuth(Boolean(authQS));
        setPageReady(true);
    }, []);

    useEffect(() => {
        async function verifyUsername() {
            try {
                await schema.validate({ email: username });
                setIsEmailValid(true);
            } catch (err) {
                setIsEmailValid(false);
            }
        }

        verifyUsername();
    }, [username]);

    useEffect(() => {
        const usernameQS = params.get('username');

        if (usernameQS) {
            setUsername(usernameQS);
        }
        dispatch(authFailureClear());
    }, [dispatch]);

    useEffect(() => {
        if (user) {
            if (
                user.profiles.filter((p: Profile) => p.profileKey === 'HR' || p.profileKey === 'STUDENT').length === 2 &&
                !(person.dynamicAttributes && person.dynamicAttributes.selectedProfile)
            ) {
                setActiveForm('profileSelect');
            } else {
                history.push('/');
            }
        }
    }, [history, user, person]);

    useEffect(() => {
        if (canUseSaml) {
            const samlData: SamlData = {
                orgId: organization.id,
                applicationId: Constants.APP_ID,
                callback: encodeURIComponent(`${window.location.origin}${window.location.pathname}${Constants.LOGIN_SAML_CALLBACK_URL}`),
            };
            dispatch(samlRequest(samlData));
        }
    }, [dynamicAttributes]);

    useEffect(() => {
        if (saml.data.url) {
            clearAuth();
            window.location.href = saml.data.url;
        }
    }, [saml.data.url]);

    const getLoginErrorMessage = (): string => {
        return isEntityNotFound() ? strings.NON_EXISTENT_USER : strings.AUTHENTICATION_FAILED;
    };

    const getResetPasswordErrorMessage = (): string => {
        return isEntityNotFound() ? strings.USER_NOT_REGISTERED : strings.RESET_PASSWORD_FAILED;
    };

    const isEntityNotFound = (): boolean => {
        const status = error && error.response && error.response.status;
        return status === Constants.statusCode.ENTITY_NOT_FOUND;
    };

    if (!pageReady) {
        return null;
    }

    return (
        <div className="login">
            <div className="content">
                {activeForm === 'login' && !byPassAuth && (
                    <div className="form">
                        <img alt="" className="logo" src={logo} />
                        <form className="form-container needs-validation" noValidate>
                            <div className="form-group">
                                <label htmlFor="emailInput">{strings.EMAIL}:</label>
                                <input
                                    className="form-control"
                                    disabled={canUseSaml}
                                    id="emailInput"
                                    onChange={handleUsernameChange}
                                    placeholder={strings.TYPE_YOUR_EMAIL}
                                    required
                                    type="email"
                                    value={username}
                                />
                            </div>
                            <div className="form-group pos-r">
                                <label htmlFor="passwordInput">{strings.PASSWORD}:</label>
                                <button className="btn btn-link" onClick={handleVisibilityChange} type="button">
                                    <i className="material-icons pos-a">{visibilityPasswordIcon}</i>
                                </button>
                                <input
                                    className="form-control"
                                    disabled={canUseSaml}
                                    id="passwordInput"
                                    onChange={handlePasswordChange}
                                    placeholder={strings.TYPE_YOUR_PASSWORD}
                                    required
                                    type={visibilityPasswordType}
                                />
                            </div>
                            {hasError && (
                                <p className="alert alert-danger m--none p--xxs">
                                    <i className="material-icons va-m m-right--xxs">{Constants.icons.ALERT_ERROR}</i>
                                    {getLoginErrorMessage()}
                                </p>
                            )}
                            <button
                                className="btn btn-primary btn-block m-top--sm"
                                disabled={loading || !isEmailValid}
                                onClick={handleSubmit}
                                type="submit"
                            >
                                {loading && <span>{strings.PROCESSING}</span>}
                                {!loading && <span>{strings.ENTER}</span>}
                            </button>
                            <button
                                className="btn btn-link btn-sm btn-block m-top--sm"
                                disabled={canUseSaml}
                                onClick={handleForgotPassword}
                                type="button"
                            >
                                {strings.FORGOT_PASSWORD}
                            </button>
                        </form>
                    </div>
                )}
                {activeForm === 'forgotPassword' && (
                    <div className="form container">
                        <i className="material-icons-outlined mb-4 ico">vpn_key</i>
                        {forgotPasswordMode && !loading && !hasError && (
                            <>
                                <h1 className="mb-4 text-center mr-5 ml-5">{strings.RESET_PASSWORD_SUCCESS_TITLE}</h1>
                                <p className="mb-5 text-center mr-5 ml-5">{strings.RESET_PASSWORD_SUCCESS_CTA}</p>
                            </>
                        )}
                        {(!forgotPasswordMode || loading || (forgotPasswordMode && !loading && hasError)) && (
                            <>
                                <h1 className="mb-4 text-center">{strings.FORGOT_PASSWORD}</h1>
                                <p className="mb-0 text-center">{strings.FORGOT_PASSWORD_CTA1}</p>
                                <p className="mb-5 text-center">{strings.FORGOT_PASSWORD_CTA2}</p>
                            </>
                        )}
                        <form className="form-container needs-validation" noValidate>
                            {(!forgotPasswordMode || loading || (forgotPasswordMode && !loading && hasError)) && (
                                <div className="form-group">
                                    <input
                                        className="form-control"
                                        id="emailInput"
                                        onChange={handleUsernameChange}
                                        placeholder="Seu email"
                                        required
                                        type="email"
                                        value={username}
                                    />
                                </div>
                            )}
                            {forgotPasswordMode && !loading && hasError && (
                                <p className="alert alert-danger text-center m--none p-v--xxs">{getResetPasswordErrorMessage()}</p>
                            )}
                            {(!forgotPasswordMode || loading || (forgotPasswordMode && !loading && hasError)) && (
                                <>
                                    <button
                                        className="btn btn-primary btn-block m-top--sm"
                                        disabled={loading || !isEmailValid}
                                        onClick={handleSubmitForgotPassword}
                                        type="submit"
                                    >
                                        {loading && <span>{strings.PROCESSING}</span>}
                                        {!loading && <span>{strings.RESET_PASSWORD}</span>}
                                    </button>
                                    <button className="btn btn-link btn-sm btn-block m-top--sm" onClick={handleCancelForgotPassword} type="button">
                                        {strings.CANCEL}
                                    </button>
                                </>
                            )}
                            {forgotPasswordMode && !loading && !hasError && (
                                <button className="btn btn-primary btn-block m-top--sm" onClick={handleCancelForgotPassword} type="button">
                                    {strings.UNDERSTAND}
                                </button>
                            )}
                        </form>
                    </div>
                )}
                {activeForm === 'profileSelect' && (
                    <div className="form container">
                        <i className="material-icons mb-4 ico">people_outline</i>
                        <h1 className="mb-4 text-center mr-5 ml-5">{strings.PROFILE_SELECT_CTA}</h1>
                        <form className="form-container needs-validation" noValidate>
                            <div
                                className="option-container"
                                onClick={() => setSelectedProfile('HR')}
                                onKeyDown={() => {}}
                                role="button"
                                tabIndex={0}
                            >
                                <span className={`${selectedProfile === 'HR' ? 'active' : null}`}>{strings.ACCESS_AS_ADMIN}</span>
                                {selectedProfile === 'HR' && <i className="material-icons-outlined align-middle">done</i>}
                            </div>
                            <div
                                className="option-container"
                                onClick={() => setSelectedProfile('STUDENT')}
                                onKeyDown={() => {}}
                                role="button"
                                tabIndex={0}
                            >
                                <span className={`${selectedProfile === 'STUDENT' ? 'active' : null}`}>{strings.ACCESS_AS_USER}</span>
                                {selectedProfile === 'STUDENT' && <i className="material-icons-outlined align-middle">done</i>}
                            </div>
                            <button
                                className="btn btn-primary btn-block m-top--sm"
                                disabled={selectedProfile === null || selectedProfile === ''}
                                onClick={handleConfirmProfileSelect}
                                type="button"
                            >
                                {strings.ENTER}
                            </button>
                            <button className="btn btn-link btn-sm btn-block m-top--sm" onClick={handleCancelProfileSelect} type="button">
                                {strings.CANCEL}
                            </button>
                        </form>
                    </div>
                )}
            </div>
            <Footer />
        </div>
    );
};

export default Login;
